import type { ContextValues, UserPublicID } from "@atoms/atom-types";
import { CONTEXT_KEY, DefaultEntities } from "@atoms/atom-types";
import { type ReactNode, useMemo } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface UserState {
  userId: UserPublicID | null;
  setUserId: (id: UserPublicID | null) => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      userId: null,
      setUserId: (id) => set({ userId: id }),
    }),
    {
      name: "user-store",
    },
  ),
);

export const useMyUserId = (throwIfNotFound = true): UserPublicID => {
  const impersonationUserId = localStorage.getItem("impersonation-user-id");
  const userId = useUserStore((state) => state.userId);

  if (impersonationUserId) {
    return impersonationUserId as UserPublicID;
  }

  if (!userId && throwIfNotFound) throw new Error("No user ID found");
  return userId as UserPublicID;
};

export const usePersonalContext = (): ContextValues => {
  const userId = useMyUserId();
  if (!userId) throw new Error("No user ID found");
  return useMemo(
    () => ({
      [CONTEXT_KEY(DefaultEntities.User)]: userId,
    }),
    [userId],
  );
};

/**
 * Wraps the children in a component that will ensure that the user ID is present.
 */
export const AuthenticatedApp = ({
  children,
  fallback = null,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) => {
  const userId = useMyUserId(false);
  if (!userId) return fallback;
  return children;
};
